import { useState, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import InstallInstructions from './InstallInstructions';
import { isMobileDevice } from '../utils/pwa';

const DashboardLayout = () => {
  const { currentUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showInstallBanner, setShowInstallBanner] = useState(true);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close install banner
  const closeInstallBanner = () => {
    setShowInstallBanner(false);
    // Remember that user closed the banner in this session
    sessionStorage.setItem('installBannerClosed', 'true');
  };

  // Check if banner was closed in this session
  useEffect(() => {
    if (sessionStorage.getItem('installBannerClosed') === 'true') {
      setShowInstallBanner(false);
    }
  }, []);

  return (
    <div className="dashboard-layout">
      {/* Mobile header with hamburger menu */}
      {isMobile && (
        <header className="mobile-header">
          <div className="container">
            <div className="mobile-header-content">
              <div className="mobile-logo">
                <img src="/logo192.png" alt="WellFed Cats" className="mobile-logo-img" />
                <span>WellFed Cats</span>
              </div>
              <button 
                className="mobile-menu-toggle" 
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                </span>
              </button>
            </div>
          </div>
        </header>
      )}

      <div className="dashboard-container">
        {/* Sidebar navigation (desktop or mobile menu) */}
        <aside className={`dashboard-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''} ${isMobile ? 'mobile' : ''}`}>
          <div className="sidebar-header">
            {!isMobile && (
              <div className="sidebar-logo">
                <img src="/logo192.png" alt="WellFed Cats" />
                <h2>WellFed Cats</h2>
              </div>
            )}
            <div className="user-info">
              <div className="user-avatar">
                {currentUser?.name?.charAt(0) || '?'}
              </div>
              <div className="user-details">
                <span className="user-name">{currentUser?.name || 'User'}</span>
                <span className="user-email">{currentUser?.email || 'user@example.com'}</span>
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <ul>
              <li>
                <NavLink 
                  to="/dashboard" 
                  end
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => isMobile && setIsMobileMenuOpen(false)}
                >
                  <i className="nav-icon">🏠</i>
                  <span>Dashboard</span>
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="/dashboard/cats" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => isMobile && setIsMobileMenuOpen(false)}
                >
                  <i className="nav-icon">🐱</i>
                  <span>All Cats</span>
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="/dashboard/feeding-request/open" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => isMobile && setIsMobileMenuOpen(false)}
                >
                  <i className="nav-icon">🍽️</i>
                  <span>My Requests</span>
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="/dashboard/feeding-request/processed" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => isMobile && setIsMobileMenuOpen(false)}
                >
                  <i className="nav-icon">📝</i>
                  <span>Feeding History</span>
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="/dashboard/settings" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => isMobile && setIsMobileMenuOpen(false)}
                >
                  <i className="nav-icon">⚙️</i>
                  <span>Settings</span>
                </NavLink>
              </li>
            </ul>
          </nav>

          {/* Show install instructions in sidebar for mobile */}
          {isMobileDevice() && (
            <div className="sidebar-install-banner">
              <InstallInstructions />
            </div>
          )}
        </aside>

        {/* Main content area */}
        <main className="dashboard-main">
          {/* Install banner for mobile devices */}
          {isMobileDevice() && showInstallBanner && (
            <div className="dashboard-install-banner">
              <p>
                <strong>Install this app</strong> on your home screen for a better experience
              </p>
              <div className="banner-actions">
                <InstallInstructions />
                <button onClick={closeInstallBanner} className="close-banner">
                  ✕
                </button>
              </div>
            </div>
          )}
          
          <div className="dashboard-content">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Overlay for mobile menu */}
      {isMobile && isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay" 
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default DashboardLayout; 